import React, {useCallback, useRef} from 'react'
import {Link} from 'react-router-dom'
import {withTranslation} from 'react-i18next'
import {Transition} from 'react-transition-group'
import {Tooltip} from 'antd'

import MODES from 'constants/MODES'
import {searchService} from 'services'
import requestHandler from 'utils/requestHandler'
import getPageSize from 'utils/getPageSize'
import {CloseIcon} from 'components/CustomIcons'
import useSearchContext from 'components/SearchContext/useSearchContext'
import BButton from 'components/Form/Fields/BButton'
import SearchMain from 'modules/SearchMain'
import Category from 'modules/Category'
import Products from 'modules/Products'

const pageSize = getPageSize()

const MainInput = ({mode, searchField, onInteract, searchValue, setSearchValue, t}) => {
  const {catTitle} = useSearchContext()

  const catTitleBadge = useRef()

  return (
    <>
      {mode === MODES.categoryItems && catTitle ? (
        <>
          <div className='searchMain-mainInput-catTitle' ref={catTitleBadge}>
            {t('in')} {catTitle}
          </div>

          <Link to={'/search/' + searchValue}>
            <button className='searchMain-mainInput-catTitle-close'>
              <CloseIcon/>
            </button>
          </Link>
        </>
      ) : ''}

      <input
        ref={searchField}
        autoComplete='off'
        onFocus={_ => onInteract(true, searchValue)}
        onBlur={_ => onInteract(false, searchValue)}
        placeholder={t('Tell us what you are looking for…')}
        onChange={e => setSearchValue(e.target.value)}
        value={searchValue}
        style={mode === MODES.categoryItems ? (
          {paddingRight: `calc(11.5em + ${catTitleBadge.current?.offsetWidth}px`}
        ) : undefined}
      />
    </>
  )
}


const SearchResultsHandler = (
  {
    t,
    onInteract,
    isOpen,
    setSearching,
    data,
    setData,
    searchValue,
    setSearchValue,
    alias,
    mode,
  },
) => {
  const searchField = useRef()
  const handleSearch = useCallback((mode, value, alias, callbackSuccess) => {
    setSearching(true)
    setSearchValue(value)

    const endpoint = mode === MODES.categoryItems ? (
      (searchValue, page) => searchService.categoryProducts(alias, searchValue, page)
    ) : (
      searchService.prosugcat
    )

    if (mode === MODES.autocomplete && !value.length) {
      setData({init: true})
    } else if (value.length > 0 && value.length < 3) {
      setData({tooShort: true})
    } else {
      setData({loading: true})

      requestHandler(_ => endpoint(value, {take: pageSize}), 'json')
        .then(r => {
          if (value === searchField.current.value) {
            if (r.success) {
              setData(r.data)
              if (callbackSuccess) callbackSuccess(r.data)
            } else {
              setData({error: 'request error'})
            }
          }
        })
    }
  }, [setData, setSearchValue, setSearching])

  const onClickSearchBtn = e => {
    if (!searchValue || searchValue.length < 3) {
      e.preventDefault()
      searchField.current.focus()
    }
  }

  return (
    <div className='searchMain'>
      <div id='searchMainField'>
        {isOpen && data.tooShort && (
          <Tooltip
            title={t('Write min. 3 characters')}
            placement='topLeft'
            visible={true}
            overlayClassName='search-valueTooShort'/>
        )}

        <MainInput
          mode={mode}
          searchField={searchField}
          onInteract={onInteract}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          t={t}
        />

        <Link
          to={loc => (searchValue && searchValue.length >= 3) ? `/search/${searchValue}` : loc}
          onClick={onClickSearchBtn}
        >
          <BButton yellow>{t('SEARCH')}</BButton>
        </Link>
      </div>

      <div id='searchMain-results' className={isOpen ? 'searchMain-resultsOpen' : ''}>
        <Transition timeout={{exit: 650}} in={isOpen} mountOnEnter unmountOnExit>
          {state => (mode === MODES.autocomplete ? (
            <SearchMain
              state={state}
              handleSearch={handleSearch}
              searchValue={searchValue}
              data={data}
              setData={setData}
            />
          ) : mode === MODES.categoryItems ? (
            <Category
              state={state}
              handleSearch={handleSearch}
              searchValue={searchValue}
              data={data}
              setData={setData}
              alias={alias}
            />
          ) : mode === MODES.fullSearchResults ? (
            <Products
              state={state}
              handleSearch={handleSearch}
              searchValue={searchValue}
              data={data}
              setData={setData}
            />
          ) : '')}
        </Transition>
      </div>
    </div>
  )
}

export default withTranslation()(SearchResultsHandler)
